<template>
    <section
        :id="id"
        class="cards-list"
        :class="elementClass.root"
    >
        <div class="inner">
            <h2 v-if="title" :class="elementClass.heading">{{ title }}</h2>
            <div
                v-for="(card, index) in cards"
                :key="`card-${id}-${card.id}-${index}`"
                class="card"
            >
                <div class="content">
                    <h3 v-if="card.title" :class="elementClass.card.heading">{{ card.title }}</h3>
                    <DSCFText
                        v-if="card.text"
                        class="text"
                        :content="card.text"
                    />
                    <div v-if="card.links" class="buttons with-text">
                        <NuxtLink
                            v-for="(link, key) in card.links.filter(link => link.title)"
                            :key="key"
                            v-bind="$cms.parseLink(link)"
                            :class="resolveLinkClass(key)"
                        >
                            <DSFontIcon
                                v-if="display.component === 'quick-links'"
                                :code="layout?.button?.ctaIcon ?? 'chevron-right'"
                                :icon-style="layout?.button?.ctaIconStyle ?? 'line'"
                            />
                            <template v-else>
                                <span>{{ link.title }}</span>
                            </template>
                        </NuxtLink>
                    </div>
                </div>
                <DSFrontifyAsset
                    v-if="card.image"
                    :asset="card.image"
                    fill="cover"
                />
            </div>
        </div>
    </section>
</template>

<script setup>
import { inject, computed } from '#imports';
const layout = inject('layout');
const props = defineProps({
    id: {
            type: String,
            required: true,
            default: '',
        },
        contentType: {
            type: [String, null],
            required: false,
            default: 'cardList',
        },
        internal: {
            type: [String, null],
            required: false,
            default: 'Card List',
        },
        display: {
            type: [Object],
            required: false,
            default: () => ({
                component: 'list',
                alternating: 'false',
            }),
        },
        title: {
            type: [String, null],
            required: false,
            default: '',
        },
        cards: {
            type: [Array],
            required: false,
            default: () => [],
        },
});

const elementClass = computed(()=> {
    const elementClass = {
                root: `display-${props.display.component}`,
            };

            switch (props.display.component) {
                case 'half-content':
                    Object.assign(elementClass, {
                        heading: 'h2',
                        card: {
                            heading: 'h4',
                        },
                    });

                    if (props.display.alternating === 'false') {
                        elementClass.root += ' not-alternating';
                    }
                break;

                case 'banner':
                    Object.assign(elementClass, {
                        heading: 'h2',
                        card: {
                            heading: 'h4',
                        },
                    });
                break;

                default:
                case 'list':
                    Object.assign(elementClass, {
                        root: `${elementClass.root}${props.display.alternating === 'true' ? ' alternating' : ''}`,
                        heading: 'h2',
                        card: {
                            heading: 'h4',
                        },
                    });
                break;
            }

            return elementClass;
});

const resolveLinkClass = key => {
    if (props.display.component === 'banner') {
        return `ds-btn ds-btn--small ds-btn--${key === 0 ? 'primary' : 'secondary'}`;
    }

    return `ds-btn ds-btn--large ds-btn--${key === 0 ? 'primary' : 'secondary'}`;
};
</script>
<style scoped>
.cards-list.display-half-content {
  display: grid;
  max-width: var(--desktop-max-width);
  width: 100%;
  margin: 0 auto;
}
.cards-list.display-half-content .inner {
  max-width: var(--page-max-width);
  margin: 0 auto;
  padding-left: 1.5rem;
  width: 100%;
  padding-right: 1.5rem;
}
.cards-list.display-half-content {
  padding-top: 4rem;
  padding-bottom: 4rem;
}
.cards-list.display-half-content h2 {
  margin-top: -2rem;
}
.cards-list.display-half-content .card {
  display: grid;
  grid-template-columns: repeat(2, 50%);
}
.cards-list.display-half-content .card .image,
.cards-list.display-half-content .card .video {
  grid-row-start: 1;
  aspect-ratio: 7/5;
}
.cards-list.display-half-content .card .content {
  align-self: center;
  padding: 1.5rem 4rem 1.5rem 0;
  grid-column-start: 1;
  grid-row-start: 1;
}
.cards-list.display-half-content .card .content .buttons {
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
  flex-flow: row wrap;
}
.cards-list.display-half-content .card .content .buttons > * {
  margin-top: 1rem;
}
.cards-list.display-half-content .card .content .buttons > *:not(:last-child) {
  margin-right: 1rem;
}
@media (min-width: 768px) {
  .cards-list.display-half-content:not(.not-alternating) .card:nth-child(2n) > .content {
    grid-column-start: 2;
    padding: 1.5rem 0 1.5rem 4rem;
  }
}
.cards-list.display-half-content.not-alternating .card:not(:last-child) {
  margin-bottom: 2rem;
}
.cards-list.display-half-content.not-alternating .card > .content {
  grid-column-start: 2;
  padding: 1.5rem 0 1.5rem 4rem;
}
@media (max-width: 767px) {
  .cards-list.display-half-content:not(.not-alternating) .card, .cards-list.display-half-content.not-alternating .card {
    grid-template-columns: 1fr;
  }
  .cards-list.display-half-content:not(.not-alternating) .card .image,
  .cards-list.display-half-content:not(.not-alternating) .card .video, .cards-list.display-half-content.not-alternating .card .image,
  .cards-list.display-half-content.not-alternating .card .video {
    grid-column-start: 1;
    order: -1;
  }
  .cards-list.display-half-content:not(.not-alternating) .card .image-container, .cards-list.display-half-content.not-alternating .card .image-container {
    padding-top: 56.25%;
  }
  .cards-list.display-half-content:not(.not-alternating) .card .content, .cards-list.display-half-content.not-alternating .card .content {
    grid-column-start: 1;
    grid-row-start: 2;
    padding: 2rem 0;
  }
}
.cards-list.display-list {
  display: grid;
  max-width: var(--desktop-max-width);
  width: 100%;
  margin: 0 auto;
}
.cards-list.display-list .inner {
  max-width: var(--page-max-width);
  margin: 0 auto;
  padding-left: 1.5rem;
  width: 100%;
  padding-right: 1.5rem;
}
.cards-list.display-list {
  padding-top: 4rem;
  padding-bottom: 4rem;
}
@media (max-width: 1199px) {
  .cards-list.display-list {
    padding-top: 2rem;
    padding-bottom: 2rem;
  }
}
.cards-list.display-list h2 {
  margin-top: -2rem;
}
.cards-list.display-list .card {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  grid-gap: 0 2rem;
}
.cards-list.display-list .card .image,
.cards-list.display-list .card .video {
  order: -1;
  min-width: 100%;
  padding-top: 67%;
  border-radius: 1rem;
}
.cards-list.display-list .card .content {
  grid-column-start: 2;
  align-self: center;
}
.cards-list.display-list .card .content .text {
  margin-bottom: 1.5rem;
}
@media (min-width: 1200px) {
  .cards-list.display-list .card .content {
    padding-left: 4.5rem;
  }
}
.cards-list.display-list .card .buttons {
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
}
.cards-list.display-list .card .buttons > *:not(:first-child) {
  margin-left: 1rem;
}
@media (max-width: 1199px) {
  .cards-list.display-list .card .buttons {
    flex-flow: column wrap;
  }
  .cards-list.display-list .card .buttons > * {
    margin-right: auto;
  }
  .cards-list.display-list .card .buttons > *:not(:first-child) {
    margin-left: 0;
    margin-top: 1rem;
  }
}
.cards-list.display-list .card:not(:last-child) {
  margin-bottom: 3.25rem;
}
.cards-list.display-list.alternating .card:nth-child(2n+1) {
  grid-template-columns: 1.5fr 1fr;
}
.cards-list.display-list.alternating .card:nth-child(2n+1) .content {
  order: 0;
  grid-column-start: 1;
  padding-left: 0;
}
.cards-list.display-list.alternating .card:nth-child(2n+1) .image,
.cards-list.display-list.alternating .card:nth-child(2n+1) .video {
  order: 1;
}
@media (max-width: 767px) {
  .cards-list.display-list .card {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
    grid-gap: 0;
  }
  .cards-list.display-list .card .image,
  .cards-list.display-list .card .video {
    order: 0;
  }
  .cards-list.display-list .card .content {
    grid-column-start: 1;
    grid-row-start: 2;
    padding: 1.5rem 0;
  }
  .cards-list.display-list.alternating .card:nth-child(2n+1) {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 767px) {
  .cards-list .content .h3 {
    font-size: 1.125rem;
  }
}
@media (min-width: 480px) {
  .cards-list .content .h3 {
    font-size: 1.75rem;
  }
}

:global(section.cards-list + section.text-block .inner) {
  padding-top: 0;
}
</style>
